import { MutationCache, QueryClient } from '@tanstack/react-query';
import { handleReadOnlyViolationError } from '../services/impersonationService';

/**
 * Global auth error state
 * Tracks whether an authentication error (401) has been detected
 * Used by queries to determine if placeholder data should be hidden
 */
let isAuthError = false;

/**
 * Set the global auth error flag
 * When true, queries will hide placeholder data to prevent showing stale content
 *
 * @param value - true when auth error detected, false after successful login/validation
 * @param _source - source of the auth error (reserved for future telemetry integration)
 */
export const setAuthError = (value: boolean, _source?: 'query-retry' | 'mutation-retry' | 'manual') => {
  isAuthError = value;
};

/**
 * Get the current auth error state
 * Used by queries to check if placeholder data should be hidden
 *
 * @returns true if auth error detected, false otherwise
 */
export const getAuthError = (): boolean => {
  return isAuthError;
};

/**
 * Global session expiration handler
 * Called when a 401 error is detected in any query or mutation
 */
let sessionExpirationHandler: (() => void) | null = null;

export const setSessionExpirationHandler = (handler: () => void) => {
  sessionExpirationHandler = handler;
};

/**
 * Impersonation restore handler
 * When registered (by ImpersonationContext), 401s attempt identity restoration
 * before triggering session expiration/logout.
 * Returns true if restore succeeded, false if it failed.
 */
let impersonationRestoreHandler: (() => Promise<boolean>) | null = null;

/**
 * Guard flag to prevent duplicate restore attempts when multiple 401s fire simultaneously
 */
let isRestoringImpersonation = false;

export const setImpersonationRestoreHandler = (
  handler: (() => Promise<boolean>) | null,
) => {
  impersonationRestoreHandler = handler;
  // When handler is unregistered, reset the restoring guard
  if (handler === null) {
    isRestoringImpersonation = false;
  }
};

/** @internal Exposed for test assertions only — not consumed in production code */
export const getIsRestoringImpersonation = (): boolean => {
  return isRestoringImpersonation;
};

/**
 * Attempt impersonation-aware 401 handling.
 * If an impersonation restore handler is registered, attempts identity restoration
 * before falling through to normal session expiration.
 * Returns true if the 401 was handled (restore attempted or suppressed),
 * false if normal 401 handling should proceed.
 */
const tryHandleImpersonation401 = (source: 'query-retry' | 'mutation-retry'): boolean => {
  if (impersonationRestoreHandler && !isRestoringImpersonation) {
    isRestoringImpersonation = true;
    impersonationRestoreHandler()
      .then(restored => {
        isRestoringImpersonation = false;
        if (restored) {
          queryClient.invalidateQueries().catch((err: unknown) => {
            console.error('QueryClient: Failed to invalidate queries after restore:', err);
          });
        } else {
          setAuthError(true, source);
          queryClient.clear();
          if (sessionExpirationHandler) {
            sessionExpirationHandler();
          }
        }
      })
      .catch(() => {
        isRestoringImpersonation = false;
        setAuthError(true, source);
        queryClient.clear();
        if (sessionExpirationHandler) {
          sessionExpirationHandler();
        }
      });
    return true;
  }

  if (isRestoringImpersonation) {
    return true;
  }

  return false;
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes
      staleTime: 1000 * 60 * 5,
      // Keep unused data in cache for 10 minutes
      gcTime: 1000 * 60 * 10,
      // Retry failed requests up to 2 times
      retry: (failureCount, error: any) => {
        // Check for authentication errors (401 Unauthorized)
        if (error?.status === 401 || error?.response?.status === 401) {
          // Attempt impersonation restore before triggering session expiration
          if (tryHandleImpersonation401('query-retry')) {
            return false;
          }

          // Normal (non-impersonation) 401 handling
          setAuthError(true, 'query-retry');
          queryClient.clear();
          if (sessionExpirationHandler) {
            sessionExpirationHandler();
          }

          return false;
        }

        // Don't retry on other 4xx client errors
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        return failureCount < 2;
      },
      // Refetch on window focus in production for fresh data
      // Disabled in development to prevent issues with hot reload
      refetchOnWindowFocus: process.env.NODE_ENV === 'production',
      // Background refetch disabled globally (individual queries can override)
      // Was causing loops - queries should opt-in to polling explicitly
      refetchInterval: false,
    },
    mutations: {
      // Retry failed mutations once
      retry: (failureCount, error: any) => {
        // Check for authentication errors (401 Unauthorized)
        if (error?.status === 401 || error?.response?.status === 401) {
          // Attempt impersonation restore before triggering session expiration
          if (tryHandleImpersonation401('mutation-retry')) {
            return false;
          }

          // Normal (non-impersonation) 401 handling
          setAuthError(true, 'mutation-retry');
          queryClient.clear();
          if (sessionExpirationHandler) {
            sessionExpirationHandler();
          }

          return false;
        }

        // Don't retry on other 4xx client errors (including 403)
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }

        return failureCount < 1;
      },
    },
  },
  mutationCache: new MutationCache({
    onError: (error) => {
      // Show user-friendly toast for read-only impersonation violations
      handleReadOnlyViolationError(error);
    },
  }),
});
